<template>
  <div class="autocomplete">
    <div :class="['pthv-search-field', {'form': useRdds, 'form--inline': useRdds }]">
      <SearchInput
        v-model="search"
        @input="onChange"
        @keydown.down="onArrowDown"
        @keydown.up="onArrowUp"
        @keydown.enter="onEnter">
      </SearchInput>
      <button v-if="showSubmitButton" class="pthv-search-field-submit" :title="$t('search')" @click="onEnter">
        {{ $t("search") }}
      </button>
      <div v-if="geoloc" class="form-actions">
        <button class="pthv-search-field-locate button" :aria-label="$t('locateme')" :title="$t('locateme')" @click="geolocUser">
          <locationArrowSVG></locationArrowSVG>
        </button>
      </div>
    </div>
    <ul v-show="isOpen" ref="results" class="autocomplete-results">
      <li class="loading" v-if="isLoading">
        {{ $t("loading") }}...
      </li>
      <div v-else v-for="(busStop, i) in suggestions">
        <li
          :key="i"
          :ref="i"
          @click="
            setChoice(i);
            setResult();
          "
          :class="{ 'is-active': isActive(i) }"
          class="autocomplete-result"
        >
          <div class="autocomplete-suggestion" :data-name="busStop.name">
            <div class="arret">
              <span
                :class="busStop.isCommercial ? 'icon-zone' : 'icon-arret'"
              ></span>
              <span v-html="hihlightedName(busStop)"></span>
              <span v-if="showCities && busStop.city">- {{ busStop.city }}</span>
            </div>
          </div>
        </li>

        <li
          v-for="(dest, j) in busStop.destinations"
          :key="i + '-' + j"
          :ref="i + '-' + j"
          @click="
            setChoice(i + '-' + j);
            setResult();
          "
          class="autocomplete-result"
          :class="{ 'is-active': isActive(i + '-' + j) }"
        >
          <div
            class="autocomplete-suggestion"
            :data-destref="dest.id"
            :data-name="dest.name"
          >
            <div class="dest">
              <span class="icon-dest"></span>
              <span
                class="nLign"
                :style="
                  `background: ${dest.destService.colors.bgcolor}; color: ${dest.destService.colors.color}`
                "
                >{{ dest.destService.mnemo }}</span
              ><span class="dirLign">{{ dest.name }}</span>
            </div>
          </div>
        </li>
      </div>
    </ul>
    <h2 v-show="showStopName" class="stopNameInfo">
      <span class="stopNameLabel">{{ selectedStopName }}</span>
      <template v-if="showStopId">
        <br><span class="stopNameId">{{ selectedStopId }}</span>
      </template>
    </h2>
  </div>
</template>

<style lang="scss">
@use "sass:math";
$assetsPath : "../assets/";
$base-unit: 8 !default;
@function num($value) {
  @return math.div($value, ($value * 0 + 1));
}

@function unite($value) {
  @return (num($value) * $base-unit) + px;
}

.autocomplete {
  position: relative;
  width: 100%;
  input {
    width: 100%;
  }
}

.stopNameInfo {
  margin: 0.5em;
}

.stopNameLabel {
  font-weight: bold;
}

.stopNameId {
  font-size: 0.8em;
  font-style: italic;
}

.pthv-search-field {
  display: flex;

  input {
    padding: 0.5rem;
  }

  &-locate {
    cursor: pointer;
    font-size: 0;
    background: transparent;
    outline: none;
    border-block: 1px solid;
    border-inline-start: none;
    border-inline-end: 1px solid;

    svg {
      fill: #495560;
      width: 1.5rem;
    }

    &:hover {
      svg {
        fill: #000;
      }
    }
  }
}
.autocomplete-results {
  padding: 0;
  margin: 0;
  border: 1px solid #CCC;
  max-height: 200px;
  width: 100%;
  overflow: auto;
  background-color: #FFF;
}

.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 4px 2px;
  cursor: pointer;
}

.autocomplete-result:hover {
  background-color: #DDD;
}

.autocomplete-result.is-active {
  background-color: #DDD;
}

.autocomplete-suggestions {
  max-height: 320px;
  min-width: 245px;
}
.autocomplete-suggestion {
  cursor: pointer;
  font-size: 1rem;
  padding: unite(1) 0;
  font-size: unite(1.75);
  &:not(:last-child) {
    border-bottom: 1px solid rgba(#000, 0.2);
  }
  > div {
    margin: 0 unite(1);
  }
  .arret {
    span:first-child {
      margin-right: unite(1);
    }
  }
  .dest {
    > img {
      margin-right: unite(1);
      margin-left: unite(1);
      display: inline-block;
      vertical-align: middle;
    }
    span:first-child {
      margin-right: unite(1);
      // margin-left: unite(1);
    }
  }
  [class^="icon-"] {
    width: unite(4);
    height: unite(4);
    padding: unite(1);
  }
  span {
    padding: unite(0.5);
    //display: table-cell;
    vertical-align: middle;
  }
  .nLign {
    margin-right: unite(0.5);
    min-width: unite(3);
    display: inline-block;
    vertical-align: middle;
    text-align: center;
  }
}
.icon-zone {
  background: url(#{$assetsPath}icon-zone.svg) no-repeat center center transparent;
}
.icon-arret {
  background: url(#{$assetsPath}icon-arret.svg) no-repeat center center
    transparent;
}
.icon-dest {
  background: url(#{$assetsPath}icon-dest.svg) no-repeat center center transparent;
}
</style>

<script>
import Fuse from "fuse.js";
import { scrollParentToChild } from "../lib/utils.ts";
// Other components
import locationArrowSVG from "./svg/locationArrowSVG";
//Components
import SearchInput from "./rdds/SearchInput";

export default {
  name: "ApthSearch",
  components: {
    locationArrowSVG,
    SearchInput,
  },
  props: {
    showStopName: {
      type: Boolean,
      required: false,
      default: true
    },
    showStopId: {
      type: Boolean,
      required: false,
      default: false
    },
    items: {
      type: Object,
      required: false,
      default: () => []
    },
    lines: {
      type: Object,
      required: true,
      default: () => {}
    },
    minChars: {
      type: Number,
      required: false,
      default: () => 3
    },
    showCities: {
      type: Boolean,
      required: false,
      default: () => true
    },
    selectedStop: {
      type: Object,
      required: false,
      default: () => null
    },
    geoloc: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSubmitButton: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  computed: {
    selectedStopId(){
      let strId = "";
      if (this.selectedStop){
        strId = this.selectedStop.ref
        if (this.selectedStop.isCommercial && this.selectedStop.spoints.length > 0){
          let childRefs = this.selectedStop.spoints
            .map((spoint) => spoint.ref)
            .join(", ");
          strId +=  " (" + childRefs + ")";
        }
      }
      return strId;
    },
    selectedStopName(){
      let strName = "";
      if (this.selectedStop){
        strName = this.selectedStop.name;
      }
      return strName;
    },
    fuseStops() {
      let stops = this.setDestinations(this.items);
      return autocompleteStopsInit(stops, this.showStopId);
    },
    selectedKey() {
      return this.choiceIds[this.choiceId];
    },
    choiceIds() {
      //Construct array of suggestion refs (used for tracking arrows keys navigation).
      let choices = [];
      for (let i = 0; i < this.suggestions.length; i++) {
        choices.push(i);
        let destinations = this.suggestions[i].destinations;
        if (destinations){
          for (let j = 0; j < destinations.length; j++) {
            choices.push(`${i}-${j}`);
          }
        }
      }
      return choices;
    }
  },
  data() {
    return {
      search: "",
      suggestions: [],
      isOpen: false,
      isLoading: false,
      choiceId: 0
    };
  },
  methods: {
    geolocUser() {
      this.$emit("geoloc-user");
    },
    onChange() {
      let term = this.search.toLowerCase();
      if (term.length >= this.minChars) {
        this.isOpen = true;
        this.suggestions = this.fuseStops.search(term);
      } else {
        this.suggestions = [];
      }
    },
    setChoice(ref) {
      this.choiceId = this.choiceIds.indexOf(ref);
    },
    setResult() {
      let element = this.$refs[this.selectedKey];
      if (!Array.isArray(element) || element.length < 1) {
        return;
      }
      element = element[0];
      let data = element.firstChild.dataset;

      let busStopIdx = (this.selectedKey + "").split("-")[0];
      let busStop = this.suggestions[busStopIdx].item;

      let result = {
        lat: busStop.x,
        lng: busStop.y,
        stopref: busStop.ref,
        name: data.name
      };
      if (data.destref) {
        result["destref"] = data.destref;
      }
      this.$emit("search", result);

      this.search = data.name;
      this.isOpen = false;
      this.choiceId = 0;
    },
    isActive(key) {
      let isActive = key === this.choiceIds[this.choiceId];
      return isActive;
    },
    onArrowDown() {
      if (this.choiceIds.length > this.choiceId) {
        this.choiceId = this.choiceId + 1;
        if (this.$refs[this.selectedKey]) {
          scrollParentToChild(
            this.$refs["results"],
            this.$refs[this.selectedKey][0]
          );
        }
      }
    },
    onArrowUp() {
      if (this.choiceId > 0) {
        this.choiceId = this.choiceId - 1;
        if (this.$refs[this.selectedKey]) {
          scrollParentToChild(
            this.$refs["results"],
            this.$refs[this.selectedKey][0],
            false
          );
        }
      }
    },
    onEnter() {
      this.setResult();
    },
    handleClickOutside(evt) {
      if (!this.$el.contains(evt.target)) {
        this.isOpen = false;
        this.choiceId = 0;
      }
    },
    hihlightedName(found) {
      if (!this.search){
        return "";
      }
      let search = this.search.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
      let re = new RegExp("(" + search.split(" ").join("|") + ")", "gi");
      let resStr = found.item.name.replace(re, "<b>$1</b>");
      if (this.showStopId){
        resStr += " - " + found.item.refs.replace(re, "<b>$1</b>");
      }
      return resStr;
    },
    setDestinations(items) {
      let stops = [];
      for (let [i, stop] of Object.entries(items)) {
        stop["destinations"] = this.makeDestinations(stop);
        stops.push(stop);
      }
      return stops;
    },
    makeDestinations(busStop) {
      //TODO : merge this in initGtfsData.js ?
      let dests = [];
      if (busStop.isCommercial) {
        for (let i in busStop.spoints) {
          let spoint = busStop.spoints[i];
          for (let j in spoint.dests) {
            let dest = spoint.dests[j];
            dest.destService = this.lines[dest.service];
            dests.push(dest);
          }
        }
        return dests;
      }

      if (busStop.dests && busStop.dests.length > 0) {
        for (let i in busStop.dests) {
          let dest = busStop.dests[i];
          dest.destService = this.lines[dest.service];
          dests.push(dest);
        }
        return dests;
      }

      for (let i in busStop.lines) {
        let line = busStop.lines[i];
        dests.push({
          name: line.name,
          id: line.ref,
          service: line,
          destService: this.lines[line]
        });
      }
      return dests;
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  destroyed() {
    document.removeEventListener("click", this.handleClickOutside);
  }
};

function autocompleteStopsInit(busStops, showStopId) {
  let stops = [];
  for (var i in busStops) {
    let busStop = busStops[i];
    let refs = busStop.ref;
    if (busStop.isCommercial) {
      for (let spoint of busStop.spoints) {
        refs = refs + " " + spoint.ref;
      }
    }
    // busStop['name'] = busStop.name.toLowerCase() + " - " +busStop.ref.toLowerCase();
    busStop['name'] = busStop.name.toLowerCase();
    busStop['refs'] = refs.toLowerCase();
    stops.push(busStop);
  }
  let fuseStops = new Fuse(stops, {
    isCaseSensitive: true, // choisir le plus performant puisque tout est en minuscules
    threshold: 0.2, // default : 0.6 (0 : exact, 1 : retourne tout)
    // location: 0, // this is the default (on cherche depuis le début)
    distance: 500, // default : 100 (nombre de caractères sur lesquels on cherche depuis `location`, 500 * 0.2 = 100 caractères)
    keys: showStopId ? [ 'refs', 'name' ] : ['name'], // Recherche sur le nom uniquement si showstopId = false
  });
  return fuseStops;
}
</script>
